import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useRootStoreContext } from '../../stores/RootStore';

const ModalContainerStyled = styled.div`
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    z-index: 2147483646;
    animation: fadeInModalBg 333ms linear forwards;

    @keyframes fadeInModalBg {
        from {
            background-color: rgba(0, 0, 0, 0);
        }
        to {
            background-color: rgba(0, 0, 0, 0.1);
        }
    }

    @media screen and (min-width: 768px) {
        align-items: center;
    }

    > .modal-container {
        border-radius: var(--radius) 8px 0px 0px;
        background-color: #fff;

        animation: mobileSlideUp 333ms ease-out forwards;
        will-change: height, opacity;

        @keyframes mobileSlideUp {
            from {
                height: 0%;
                opacity: 0;
            }
            to {
                height: 85vh;
                opacity: 1;
            }
        }

        @media screen and (min-width: 768px) {
            border-radius: var(--radius);
            animation: contentFadeIn 333ms linear forwards;

            @keyframes contentFadeIn {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
        }
    }
`;

const ModalWrapperStyled = styled.div<{ maxWidth: number }>`
    position: relative;
    width: 100vw;
    padding: 0px 16px 0px 16px;

    @media screen and (min-width: 768px) {
        width: ${({ maxWidth }) => `${maxWidth}px`};
        max-height: 65vh;
        border-radius: var(--radius);

        overflow-y: auto;
        -webkit-overflow-scrolling: touch; /*For smooth scrolling*/
    }
`;

const HeaderStyled = styled.div`
    position: sticky;
    top: 0px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    background-color: #fff;
    z-index: 2147483647;

    > * {
        color: #999;
    }

    > button {
        margin: 0px;
        padding: 0px;
        background: transparent;
        border: 0px;
        line-height: 0px;

        &:hover * {
            color: #1c1c1c;
            transition: color 300ms linear;
        }
    }

    h3 {
        color: #12131a;
        font-family: Playfair Display, serif;
        font-size: 28px;

        @media screen and (max-width: 768px) {
            font-size: 24px;
        }
    }
`;

type Props = {
    width?: number;
    children: React.ReactNode;
};

const Modal: FC<Props> = ({ width = 640, children }) => {
    const {
        UIStore: {
            reducer: [{ modalConfig }],
        },
    } = useRootStoreContext();
    const targetRef = useRef<any>();

    useEffect(() => {
        if (modalConfig.type !== 'None') {
            disableBodyScroll(targetRef.current, {
                reserveScrollBarGap: true,
            });
        } else {
            clearAllBodyScrollLocks();
        }

        return () => clearAllBodyScrollLocks();
    }, [modalConfig.type]);

    if (modalConfig.type === 'None') {
        return null;
    }

    return (
        <ModalContainerStyled
            onClick={() => modalConfig.onClose && modalConfig.onClose()}
            ref={targetRef}
        >
            <div className="modal-container">
                <ModalWrapperStyled
                    onClick={(e) => e.stopPropagation()}
                    maxWidth={width}
                >
                    <HeaderStyled>
                        <h3>{modalConfig.title}</h3>
                        <button
                            onClick={() =>
                                modalConfig.onClose && modalConfig.onClose()
                            }
                        >
                            <i className="material-icons">close</i>
                        </button>
                    </HeaderStyled>
                    {children}
                </ModalWrapperStyled>
            </div>
        </ModalContainerStyled>
    );
};

export default Modal;
